import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { useSidebarState } from '../hooks/useSidebarState';
import {
  Users, Tag, Loader2, ShieldCheck, BarChart3, Shield, Clock,
  ToggleLeft, Building2, Rocket, Mail, Newspaper, Database,
} from 'lucide-react';
import { toast } from 'sonner';

import { NewsManagementTab } from './admin/NewsManagementTab';
import { LaunchControlTab } from './admin/LaunchControlTab';
import { WaitlistTab } from './admin/WaitlistTab';
import { EditUserModal } from './admin/EditUserModal';
import { DeleteUserModal } from './admin/DeleteUserModal';
import { TransferOwnershipModal } from './admin/TransferOwnershipModal';
import { AccessControlTab } from './admin/AccessControlTab';
import { OverviewTab } from './admin/OverviewTab';
import { UsersTab } from './admin/UsersTab';
import { EnterprisePlansTab } from './admin/EnterprisePlansTab';
import { PromoCodesTab } from './admin/PromoCodesTab';
import { FeatureFlagsTab } from './admin/FeatureFlagsTab';
import { RegulatoryTab } from './admin/RegulatoryTab';
import { AuditLogTab } from './admin/AuditLogTab';
import { apiGet, apiPost, apiPut, apiPatch, apiDelete } from '../lib/apiClient';

const AdminPortal = ({ user, setUser }) => {
  const navigate = useNavigate();
  useSidebarState();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [promoCodes, setPromoCodes] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [tierFilter, setTierFilter] = useState('');
  const [editModal, setEditModal] = useState(null);
  const [deleteModal, setDeleteModal] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [transferModal, setTransferModal] = useState(null);
  const [transferLoading, setTransferLoading] = useState(false);
  const [newPromo, setNewPromo] = useState({ code: '', discount_percent: 50, valid_for_tiers: ['starter', 'growth'], valid_from: '', valid_to: '' });
  const [showPromoForm, setShowPromoForm] = useState(false);
  const [permissions, setPermissions] = useState(null);
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const [auditLog, setAuditLog] = useState([]);
  const [permSaving, setPermSaving] = useState(false);
  const [enterprisePlans, setEnterprisePlans] = useState([]);
  const [showPlanForm, setShowPlanForm] = useState(false);
  const [newPlan, setNewPlan] = useState({ name: '', price_monthly: '', credit_limit: '', description: '' });
  const [planLoading, setPlanLoading] = useState(false);
  const [featureFlags, setFeatureFlags] = useState(null);
  const [selectedOrg, setSelectedOrg] = useState('global');
  const [regSources, setRegSources] = useState([]);
  const [regQueue, setRegQueue] = useState([]);
  const [regQueueStats, setRegQueueStats] = useState(null);
  const [regSyncing, setRegSyncing] = useState(null);

  useEffect(() => {
    if (user && !['admin', 'owner'].includes(user.role)) {
      toast.error('Admin access required');
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const fetchStats = useCallback(async () => {
    try {
      const data = await apiGet('/api/admin/stats');
      setStats(data);
    } catch (error) {
      console.error('Failed to fetch stats');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchUsers = useCallback(async (search = '', tier = '') => {
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (tier) params.set('tier', tier);
      params.set('limit', '100');
      const data = await apiGet(`/api/admin/users?${params.toString()}`);
      setUsers(data.users || []);
      setTotalUsers(data.total || 0);
    } catch (error) {
      console.error('Failed to fetch users');
    }
  }, []);

  const fetchPromoCodes = useCallback(async () => {
    try {
      const data = await apiGet('/api/admin/promo-codes');
      setPromoCodes(data.promo_codes || []);
    } catch (error) {
      console.error('Failed to fetch promo codes');
    }
  }, []);

  const fetchPermissions = useCallback(async () => {
    setPermissionsLoading(true);
    try {
      const data = await apiGet('/api/admin/permissions');
      setPermissions(data);
    } catch (error) {
      console.error('Failed to fetch permissions');
    } finally {
      setPermissionsLoading(false);
    }
  }, []);

  const fetchAuditLog = useCallback(async () => {
    try {
      const data = await apiGet('/api/admin/audit-log?limit=30');
      setAuditLog(data.logs || []);
    } catch (error) {
      console.error('Failed to fetch audit log');
    }
  }, []);

  const fetchEnterprisePlans = useCallback(async () => {
    try {
      const data = await apiGet('/api/admin/enterprise-plans');
      setEnterprisePlans(data.plans || []);
    } catch (error) {
      console.error('Failed to fetch enterprise plans');
    }
  }, []);

  const fetchFeatureFlags = useCallback(async () => {
    try {
      const data = await apiGet('/api/admin/feature-flags');
      setFeatureFlags(data);
    } catch (error) {
      console.error('Failed to fetch feature flags');
    }
  }, []);

  const toggleFeatureFlag = async (flagId, currentValue) => {
    const isOrg = selectedOrg !== 'global';
    const path = isOrg
      ? `/api/admin/feature-flags/org/${selectedOrg}`
      : '/api/admin/feature-flags/global';

    const currentFlags = isOrg
      ? (featureFlags?.org_overrides?.find(o => o.org_id === selectedOrg)?.flags || {})
      : (featureFlags?.global_flags || {});

    const updatedFlags = { ...currentFlags, [flagId]: !currentValue };

    try {
      await apiPut(path, { flags: updatedFlags });
      toast.success(`Feature flag updated`);
      fetchFeatureFlags();
    } catch (err) {
      toast.error(err?.message || 'Failed');
    }
  };

  const resetOrgFlags = async (orgId) => {
    try {
      await apiDelete(`/api/admin/feature-flags/org/${orgId}`);
      toast.success('Org overrides removed');
      setSelectedOrg('global');
      fetchFeatureFlags();
    } catch { toast.error('Connection error'); }
  };

  const fetchRegSources = useCallback(async () => {
    try {
      const d = await apiGet('/api/regulatory/admin/sources/status');
      setRegSources(d.sources || []);
    } catch { /* ignore */ }
  }, []);

  const fetchRegQueue = useCallback(async () => {
    try {
      const d = await apiGet('/api/regulatory/admin/queue?limit=50');
      setRegQueue(d.updates || []);
    } catch { /* ignore */ }
  }, []);

  const fetchRegQueueStats = useCallback(async () => {
    try {
      const data = await apiGet('/api/regulatory/admin/queue/stats');
      setRegQueueStats(data);
    } catch { /* ignore */ }
  }, []);

  const syncSource = async (sourceId) => {
    setRegSyncing(sourceId);
    try {
      const d = await apiPost(`/api/regulatory/admin/sources/${sourceId}/sync`);
      toast.success(d.message + (d.updates_detected ? ` (${d.updates_detected} changes found)` : ''));
      fetchRegSources(); fetchRegQueue(); fetchRegQueueStats();
    } catch { toast.error('Sync failed'); }
    finally { setRegSyncing(null); }
  };

  const reviewUpdate = async (updateId, action) => {
    try {
      const d = await apiPost(`/api/regulatory/admin/queue/${updateId}/review`, { action });
      toast.success(d.message + (d.notifications_sent ? ` (${d.notifications_sent} users notified)` : ''));
      fetchRegQueue(); fetchRegQueueStats();
    } catch { toast.error('Review failed'); }
  };

  useEffect(() => {
    if (user && ['admin', 'owner'].includes(user.role)) {
      fetchStats();
      fetchUsers();
      fetchPromoCodes();
      fetchPermissions();
      fetchAuditLog();
      fetchEnterprisePlans();
      fetchFeatureFlags();
      fetchRegSources();
      fetchRegQueue();
      fetchRegQueueStats();
    }
  }, [user, fetchStats, fetchUsers, fetchPromoCodes, fetchPermissions, fetchAuditLog, fetchEnterprisePlans, fetchFeatureFlags, fetchRegSources, fetchRegQueue, fetchRegQueueStats]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchUsers(searchQuery, tierFilter);
  };

  const handleTierFilter = (tier) => {
    setTierFilter(tier);
    fetchUsers(searchQuery, tier);
  };

  const updateUser = async (userId, updates) => {
    setEditLoading(true);
    try {
      await apiPatch(`/api/admin/users/${userId}`, updates);
      toast.success('User updated successfully');
      fetchUsers(searchQuery, tierFilter);
      setEditModal(null);
    } catch (error) {
      toast.error(error?.message || 'Update failed');
    } finally {
      setEditLoading(false);
    }
  };

  const savePermissions = async (updatedApps) => {
    setPermSaving(true);
    try {
      await apiPut('/api/admin/permissions', { apps: updatedApps });
      toast.success('Permissions saved');
      fetchPermissions();
      fetchAuditLog();
    } catch (error) {
      toast.error(error?.message || 'Failed to save');
    } finally {
      setPermSaving(false);
    }
  };

  const transferOwnership = async (targetUserId) => {
    setTransferLoading(true);
    try {
      const data = await apiPost('/api/admin/transfer-ownership', { user_id: targetUserId });
      toast.success(data.message);
      if (setUser) setUser(prev => ({ ...prev, role: 'admin' }));
      fetchUsers(searchQuery, tierFilter);
      fetchAuditLog();
      setTransferModal(null);
    } catch (error) {
      toast.error(error?.message || 'Transfer failed');
    } finally {
      setTransferLoading(false);
    }
  };

  const deleteUser = async (userId) => {
    setDeleteLoading(true);
    try {
      await apiDelete(`/api/admin/users/${userId}`);
      toast.success('User deleted');
      fetchUsers(searchQuery, tierFilter);
      fetchStats();
      setDeleteModal(null);
    } catch (error) {
      toast.error(error?.message || 'Delete failed');
    } finally {
      setDeleteLoading(false);
    }
  };

  const createPromoCode = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        code: newPromo.code,
        discount_percent: newPromo.discount_percent,
        valid_for_tiers: newPromo.valid_for_tiers,
        valid_from: newPromo.valid_from ? new Date(newPromo.valid_from).toISOString() : null,
        valid_to: newPromo.valid_to ? new Date(newPromo.valid_to).toISOString() : null
      };
      await apiPost('/api/admin/promo-codes', payload);
      toast.success('Promo code created');
      fetchPromoCodes();
      setNewPromo({ code: '', discount_percent: 50, valid_for_tiers: ['starter', 'growth'], valid_from: '', valid_to: '' });
      setShowPromoForm(false);
    } catch (error) {
      toast.error(error?.message || 'Failed to create promo code');
    }
  };

  const deletePromoCode = async (code) => {
    if (!window.confirm(`Deactivate promo code "${code}"?`)) return;
    try {
      await apiDelete(`/api/admin/promo-codes/${code}`);
      toast.success('Promo code deactivated');
      fetchPromoCodes();
    } catch (error) {
      toast.error(error?.message || 'Connection error');
    }
  };

  const createEnterprisePlan = async () => {
    if (!newPlan.name || !newPlan.price_monthly || !newPlan.credit_limit) {
      toast.error('Please fill all required fields');
      return;
    }
    setPlanLoading(true);
    try {
      await apiPost('/api/admin/enterprise-plans', {
        name: newPlan.name,
        price_monthly: parseFloat(newPlan.price_monthly),
        credit_limit: parseInt(newPlan.credit_limit),
        description: newPlan.description
      });
      toast.success('Enterprise plan created');
      fetchEnterprisePlans();
      setNewPlan({ name: '', price_monthly: '', credit_limit: '', description: '' });
      setShowPlanForm(false);
    } catch (error) {
      toast.error(error?.message || 'Failed to create plan');
    } finally {
      setPlanLoading(false);
    }
  };

  const deactivateEnterprisePlan = async (planId, planName) => {
    if (!window.confirm(`Deactivate enterprise plan "${planName}"? Existing users on this plan will keep their current limits.`)) return;
    try {
      await apiDelete(`/api/admin/enterprise-plans/${planId}`);
      toast.success('Plan deactivated');
      fetchEnterprisePlans();
    } catch (error) {
      toast.error(error?.message || 'Connection error');
    }
  };

  const assignPlanToUser = async (userId, planId) => {
    try {
      await apiPost(`/api/admin/users/${userId}/assign-plan`, { plan_id: planId });
      toast.success('Enterprise plan assigned');
      fetchUsers(searchQuery, tierFilter);
      setEditModal(null);
    } catch (error) {
      toast.error(error?.message || 'Failed to assign plan');
    }
  };

  if (!user || !['admin', 'owner'].includes(user.role)) return null;

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'launch', label: 'Launch Control', icon: Rocket },
    { id: 'waitlist', label: 'Waitlist', icon: Mail },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'enterprise', label: 'Enterprise Plans', icon: Building2 },
    { id: 'features', label: 'Feature Flags', icon: ToggleLeft },
    { id: 'access', label: 'Roles & Access', icon: Shield },
    { id: 'promos', label: 'Promo Codes', icon: Tag },
    { id: 'regulatory', label: 'Regulatory Intel', icon: Database },
    { id: 'news', label: 'News', icon: Newspaper },
    { id: 'audit', label: 'Audit Log', icon: Clock }
  ];

  return (
    <div className="min-h-screen bg-[#F7F9FC] flex">
      <Sidebar user={user} setUser={setUser} activePage="admin" />

      <main className="flex-1 p-6 lg:p-8 lg:pt-24 transition-all duration-300">
        <div className="max-w-7xl">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center">
                <ShieldCheck className="w-5 h-5 text-white" />
              </div>
              <h1 className="font-outfit text-3xl font-bold text-[#0B3D91]" data-testid="admin-title">
                Admin Portal
              </h1>
            </div>
            <p className="text-slate-600">Manage users, subscriptions, and platform settings</p>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mb-6 border-b border-slate-200 pb-4">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                  activeTab === tab.id
                    ? 'bg-[#0B3D91] text-white'
                    : 'bg-white text-slate-600 hover:bg-slate-100'
                }`}
                data-testid={`admin-tab-${tab.id}`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-[#0B3D91]" />
            </div>
          ) : (
            <>
              {activeTab === 'overview' && <OverviewTab stats={stats} />}
              {activeTab === 'launch' && <LaunchControlTab />}
              {activeTab === 'waitlist' && <WaitlistTab />}
              {activeTab === 'users' && (
                <UsersTab
                  users={users}
                  totalUsers={totalUsers}
                  searchQuery={searchQuery}
                  setSearchQuery={setSearchQuery}
                  tierFilter={tierFilter}
                  onTierFilter={handleTierFilter}
                  onSearch={handleSearch}
                  onEdit={(u) => setEditModal({ ...u })}
                  onDelete={(u) => setDeleteModal(u)}
                  onTransfer={(u) => setTransferModal(u)}
                  currentUserRole={user?.role}
                />
              )}
              {activeTab === 'enterprise' && (
                <EnterprisePlansTab
                  plans={enterprisePlans}
                  showForm={showPlanForm}
                  setShowForm={setShowPlanForm}
                  newPlan={newPlan}
                  setNewPlan={setNewPlan}
                  onCreate={createEnterprisePlan}
                  onDeactivate={deactivateEnterprisePlan}
                  loading={planLoading}
                />
              )}
              {activeTab === 'promos' && (
                <PromoCodesTab
                  promoCodes={promoCodes}
                  showForm={showPromoForm}
                  setShowForm={setShowPromoForm}
                  newPromo={newPromo}
                  setNewPromo={setNewPromo}
                  onCreate={createPromoCode}
                  onDelete={deletePromoCode}
                />
              )}
              {activeTab === 'access' && (
                <AccessControlTab
                  permissions={permissions}
                  loading={permissionsLoading}
                  saving={permSaving}
                  onSave={savePermissions}
                  onRefresh={fetchPermissions}
                  isOwner={user?.role === 'owner'}
                />
              )}
              {activeTab === 'features' && (
                <FeatureFlagsTab
                  featureFlags={featureFlags}
                  selectedOrg={selectedOrg}
                  setSelectedOrg={setSelectedOrg}
                  onToggle={toggleFeatureFlag}
                  onReset={resetOrgFlags}
                />
              )}
              {activeTab === 'regulatory' && (
                <RegulatoryTab
                  regSources={regSources}
                  regQueue={regQueue}
                  regQueueStats={regQueueStats}
                  regSyncing={regSyncing}
                  onSync={syncSource}
                  onReview={reviewUpdate}
                  onRefresh={() => { fetchRegQueue(); fetchRegQueueStats(); }}
                />
              )}
              {activeTab === 'news' && <NewsManagementTab />}
              {activeTab === 'audit' && <AuditLogTab auditLog={auditLog} onRefresh={fetchAuditLog} />}
            </>
          )}
        </div>
      </main>

      {editModal && (
        <EditUserModal
          userData={editModal}
          onClose={() => setEditModal(null)}
          onSave={updateUser}
          onAssignPlan={assignPlanToUser}
          loading={editLoading}
          currentUserRole={user?.role}
          enterprisePlans={enterprisePlans}
        />
      )}

      {deleteModal && (
        <DeleteUserModal
          userData={deleteModal}
          onClose={() => setDeleteModal(null)}
          onConfirm={() => deleteUser(deleteModal.user_id)}
          loading={deleteLoading}
        />
      )}

      {transferModal && (
        <TransferOwnershipModal
          userData={transferModal}
          onClose={() => setTransferModal(null)}
          onConfirm={() => transferOwnership(transferModal.user_id)}
          loading={transferLoading}
        />
      )}
    </div>
  );
};

export default AdminPortal;
